import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaApi } from './MediaApi';
import { S3FileUploader } from '@/features/upload/S3FileUploader';
import { ensureValidCredentials } from '@/features/upload/useUploadCredentials';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { pendingTextTracksStore } from './PendingTextTracksStore';
import { FileStatusType } from '@lib2/model/media/FileStatusType';
import type { TextTrackCreateRequest, ITextTrackData } from '@/lib/types/Media';
import type { TextTrackFormat, TextTrackType } from '@lib2/model/media/TextTrackTypes';

interface CreateTextTrackParams {
  req: TextTrackCreateRequest;
  file: File;
  contentType: string;
}

export function useCreateTextTrack(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ req, file, contentType }: CreateTextTrackParams) => {
      if (!selectedCId) throw new Error('No customer selected');

      // 1. Create text track record on backend → get S3 key
      const clientIngest = await MediaApi.createTextTrack(selectedCId, mediaId, req);

      if (!clientIngest.dashboard) {
        throw new Error('Backend returned no dashboard ingest data');
      }

      const { key } = clientIngest.dashboard;

      // 2. Upload file to S3
      const credentials = await ensureValidCredentials(selectedCId);
      await S3FileUploader.putObject(credentials, key, file, contentType);

      return clientIngest.dashboard.data;
    },
    onSuccess: (data, variables) => {
      if (!selectedCId) return;

      // The media view returns only ready tracks, so the new track stays
      // invisible to refetches until ingest completes (normally seconds).
      // Surface it via the pending overlay — a direct cache write would be
      // wiped by the next refetch. The create response carries the real
      // record; the request-derived fallback (fileId 0) is defensive only.
      const track: ITextTrackData = data.textTrack
        ? { ...data.textTrack, remoteUrl: data.textTrack.remoteUrl ?? null }
        : {
            fileId: 0,
            mediaId,
            fileStatusType: FileStatusType.AwaitingIngest,
            format: variables.req.format as TextTrackFormat,
            langCode: variables.req.langCode,
            label: variables.req.label,
            ttType: variables.req.ttType as TextTrackType,
            remoteUrl: null,
          };
      pendingTextTracksStore.add(mediaId, track);

      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId),
      });
    },
  });
}

export function useDeleteTextTrack(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: number) => {
      if (!selectedCId) throw new Error('No customer selected');
      await MediaApi.deleteTextTrack(selectedCId, mediaId, fileId);
    },
    onSuccess: (_data, fileId) => {
      pendingTextTracksStore.remove(mediaId, fileId);
      if (selectedCId) {
        queryClient.invalidateQueries({
          queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId),
        });
      }
    },
  });
}
